import React, { useRef, useLayoutEffect, useImperativeHandle, forwardRef } from "react"
import { Vector as VectorSource } from "ol/source"
import { Vector as VectorLayer } from "ol/layer"
import {  Fill, Style, Stroke } from "ol/style"
import { Draw, Modify, Select } from 'ol/interaction.js'
import { transform } from "ol/proj"

import { setLayerSource, getBaseMap } from '../../components/utils'
import '../../components/style.css'
import Map from "Shared/Components/Map"

const MapEdit = forwardRef(({ zones, mapExt, mode, selected, onSelect }, ref) => {    
  const mapRef = useRef()

  const addDraw = () => {
    const map = mapRef.current.map
    const drawSource = mapRef.current.drawSource

    const draw = new Draw({
      source: drawSource,
      type: "Polygon",
    })

    draw.on('drawend', function(e) {
      map.removeInteraction(draw)

      addModify()
    })

    map.addInteraction(draw)
  }

  const addModify = () => {
    const map = mapRef.current.map
    const drawSource = mapRef.current.drawSource

    const modify = new Modify({
      source: drawSource
    })

    map.addInteraction(modify)
  }

  const setMapExtent = (coordinate) => {
    mapRef.current.map.getView().fit([coordinate["x1"], coordinate["y1"] , coordinate["x2"], coordinate["y2"]], mapRef.current.map.getSize())
  }

  useLayoutEffect(() => {
    const drawSource = new VectorSource({wrapX: false})

    const drawLayer = new VectorLayer({
      source: drawSource,
      style: {
        'fill-color': 'rgba(255, 255, 255, 0.2)',
        'stroke-color': '#03b1fc',
        'stroke-width': 2,
        'circle-radius': 7,
        'circle-fill-color': '#03b1fc',
      },
    })

    const zoneLayer = new VectorLayer({
      source: new VectorSource({})
    })

    const map = getBaseMap([drawLayer, zoneLayer])

    mapRef.current = {
      "map": map,
      "drawSource": drawSource,
      "drawLayer": drawLayer,
      "zoneLayer": zoneLayer
    }

    let select = null

    switch (zones["zone_type"]) {
      case "danger_zone":
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(255, 0, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' })
        }))

        select = new Select({
          style: new Style({
            fill: new Fill({
              color: [255, 0, 0, 0.5]
            })
          }),
        })
        
        break
      case "working_zone":
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(0, 255, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(0, 255, 0, 0.1)' })
        }))

        select = new Select({
          style: new Style({
            fill: new Fill({
              color: [0, 255, 0, 0.5]
            })
          }),
        })

        break
      default:
        mapRef.current.zoneLayer.setStyle(new Style({
          stroke: new Stroke({ color: 'rgb(255, 247, 0)', width: 3 }),
          fill: new Fill({ color: 'rgba(255, 247, 0, 0.1)' })
        }))

        select = new Select({
          style: new Style({
            fill: new Fill({
              color: [255, 247, 0, 0.5]
            })
          }),
        })
        
        break
    }
    
    if (mode === 0) {
      setLayerSource(zoneLayer, zones["zones"])

      select.on('select', function (e) {
        const features = e.target.getFeatures()

        if (features.getLength() > 0) {
          features.forEach(function (feature) {
            const id = feature.getId()

            onSelect(id)
            return
          })
        }
        else {
          onSelect()
        }
      })

      map.addInteraction(select)
    }
    else if (mode === 1) {
      setLayerSource(zoneLayer, zones["zones"])
      addDraw()
    }
    else if (mode === 2) {
      setLayerSource(zoneLayer, {
        "type": "FeatureCollection",
        "features": zones["zones"]["features"].filter((i) => i["id"] !== selected)
      })

      setLayerSource(drawLayer, {
        "type": "FeatureCollection",
        "features": zones["zones"]["features"].filter((i) => i["id"] === selected)
      })
      addModify()
    }

    setMapExtent(mapExt)
  }, [])

  useImperativeHandle(ref, () => ({
    getDrawFeature: () => {
      const layer = mapRef.current.drawLayer.getSource().getFeatures()

      return layer[0].getGeometry().getCoordinates()[0].map((i) => transform(i, 'EPSG:3857', 'EPSG:4326'))
    }
  }))

  return (<>
    <Map mapRef={mapRef}/>
  </>)
})

export default MapEdit
